import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { FaInfoCircle } from 'react-icons/fa';
import CountryAutocomplete from '../components/CountryAutocomplete';
import PipCheckPanel, { usePipCheck } from './PipCheckPanel';
import {
  NATIONALITY_CATEGORIES, PIP_ASSOCIATE_OPTIONS, OCCUPATIONS,
  SOURCES_OF_INCOME, TRANSACTION_SOURCES_OF_FUNDS, PRODUCT_TYPES, DELIVERY_CHANNELS,
  INSTITUTION_TYPES, NATURE_OF_BUSINESS, BO_PIP_OPTIONS, RISK_LABEL, categoryFromCountry
} from './riskOptions';

// Drop blank strings so optional fields are omitted rather than sent as ''
// (the API validates e.g. emails and option lists strictly).
const clean = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => !(typeof v === 'string' && v.trim() === ''))
);

// Dropdown option showing the risk the KYC and Risk Assessment Module gives it.
const riskOption = (o) => (
  <option key={o.v} value={o.v}>{o.label} ({RISK_LABEL[o.risk]} risk)</option>
);

const CUSTOMER_TYPES = [
  { v: 'individual',      label: 'Individual' },
  { v: 'sme',             label: 'SME' },
  { v: 'agent',           label: 'Agent' },
  { v: 'corporate',       label: 'Corporate' },
  { v: 'partner',         label: 'Partner' },
  { v: 'vas_provider',    label: 'VAS Provider' },
  { v: 'internal_wallet', label: 'Internal Wallet' }
];

const ID_TYPES = [
  { v: 'national_id',     label: 'National ID' },
  { v: 'passport',        label: 'Passport' },
  { v: 'drivers_licence', label: "Driver's licence" },
  { v: 'refugee_id',      label: 'Refugee ID' }
];

const INCOME = [
  { v: 'below_5k',    label: 'Below N$5,000' },
  { v: '5k_to_15k',   label: 'N$5,000 – N$15,000' },
  { v: 'above_15k',   label: 'Above N$15,000' }
];

// Inline-error styles: red border + red glow + light red bg.
// boxShadow is set inline so it wins over the global `.form-group input:focus`
// rule (inline style beats class:pseudo-class for the same property).
const ERR_STYLE = {
  borderColor: '#d32f2f',
  borderWidth: '2px',
  backgroundColor: '#fff5f5',
  boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.22)',
  outline: 'none'
};

// Edit mode: a stored value only if it is one of the current dropdown
// codes — legacy codes start blank so the user picks a current option.
const opt = (list, v) => (v && list.some((o) => o.v === v) ? v : '');
const str = (v) => (v === null || v === undefined ? '' : String(v));
// Edit mode sends blanks as null so a cleared field is cleared on the server.
const nullBlank = (obj) => Object.fromEntries(
  Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v])
);

const BLANK_UBO = {
  first_name: '', last_name: '', nationality: '', nationality_category: '',
  pip_status: 'no', beneficial_interest_pct: ''
};

// `customer` (the customer details object) switches the form to edit mode:
// every field starts from what was captured and Update Customer saves it.
function CustomerForm({ onCreated, onCancel, customer = null, onUpdated }) {
  const isEdit = !!customer;
  const ci = customer?.individual || {};
  const cb = customer?.business || {};
  const [customerType, setCustomerType] = useState(customer?.customer_type || 'individual');
  const [externalRef, setExternalRef]  = useState(str(customer?.external_reference));
  const [kycTier, setKycTier]          = useState(customer?.kyc_tier || 'lite');
  const [productType, setProductType]         = useState(opt(PRODUCT_TYPES, customer?.product_type));
  const [deliveryChannel, setDeliveryChannel] = useState(opt(DELIVERY_CHANNELS, customer?.delivery_channel));
  const [submitting, setSubmitting]    = useState(false);
  const [error, setError]              = useState(null);
  const [errors, setErrors]            = useState({}); // per-field validation errors

  // Client (individual). The sheet's risk dropdowns start empty so every one
  // is a deliberate choice.
  const [ind, setInd] = useState(isEdit && customer.individual ? {
    first_name: str(ci.first_name), middle_name: str(ci.middle_name), last_name: str(ci.last_name),
    maiden_name: str(ci.maiden_name), date_of_birth: str(ci.date_of_birth).slice(0, 10),
    id_type: ci.id_type || 'national_id', id_number: str(ci.id_number),
    nationality: str(ci.nationality), nationality_category: opt(NATIONALITY_CATEGORIES, ci.nationality_category),
    country_of_residence: str(ci.country_of_residence),
    pip_associate_relationship: ci.pip_associate_relationship === 'no'
      ? '' : opt(PIP_ASSOCIATE_OPTIONS, ci.pip_associate_relationship),
    employment_status: opt(OCCUPATIONS, ci.employment_status),
    source_of_funds: opt(SOURCES_OF_INCOME, ci.source_of_funds),
    transaction_source_of_funds: opt(TRANSACTION_SOURCES_OF_FUNDS, ci.transaction_source_of_funds),
    // Kept as captured (may be blank) so saving untouched doesn't invent a band
    monthly_income_band: opt(INCOME, ci.monthly_income_band)
  } : {
    first_name: '', middle_name: '', last_name: '', maiden_name: '',
    date_of_birth: '', id_type: 'national_id', id_number: '',
    nationality: '', nationality_category: '', country_of_residence: 'Namibia',
    pip_associate_relationship: '',
    employment_status: '', source_of_funds: '', transaction_source_of_funds: '',
    monthly_income_band: '5k_to_15k'
  });

  // Legal person
  const [biz, setBiz] = useState(isEdit && customer.business ? {
    legal_name: str(cb.legal_name), trading_name: str(cb.trading_name),
    registration_number: str(cb.registration_number), tax_number: str(cb.tax_number),
    business_type: opt(INSTITUTION_TYPES, cb.business_type),
    nature_of_business: opt(NATURE_OF_BUSINESS, cb.nature_of_business),
    country_of_incorporation: str(cb.country_of_incorporation), source_of_funds: str(cb.source_of_funds),
    primary_contact_email: str(cb.primary_contact_email), primary_contact_phone: str(cb.primary_contact_phone)
  } : {
    legal_name: '', trading_name: '', registration_number: '',
    tax_number: '', business_type: '', nature_of_business: '',
    country_of_incorporation: 'Namibia', source_of_funds: '',
    primary_contact_email: '', primary_contact_phone: ''
  });
  const [directors, setDirectors] = useState(
    cb.directors?.length
      ? cb.directors.map(d => ({ first_name: str(d.first_name), last_name: str(d.last_name), role_title: str(d.role_title) }))
      : [{ first_name: '', last_name: '', role_title: '' }]
  );
  const [shareholders, setShareholders] = useState(
    cb.shareholders?.length
      ? cb.shareholders.map(x => ({ full_name: str(x.full_name), percentage: str(x.percentage) }))
      : [{ full_name: '', percentage: '' }]
  );
  const [ubos, setUbos] = useState(
    cb.ubos?.length
      ? cb.ubos.map(u => ({
          first_name: str(u.first_name), last_name: str(u.last_name), nationality: str(u.nationality),
          nationality_category: opt(NATIONALITY_CATEGORIES, u.nationality_category),
          pip_status: opt(BO_PIP_OPTIONS, u.pip_status) || (u.is_pep_flag ? 'yes' : 'no'),
          beneficial_interest_pct: str(u.beneficial_interest_pct)
        }))
      : [BLANK_UBO]
  );

  const isInd = customerType === 'individual';

  // PIP / Associate of a PIP: worked out from the PIP check decisions.
  const pipCheck = usePipCheck(ind, isInd, customer?.pip_matches);
  const firstAccepted = pipCheck.acceptedAssociates[0];
  useEffect(() => {
    // Suggest the relationship from the accepted associate record.
    if (firstAccepted && !ind.pip_associate_relationship && firstAccepted.suggested_relationship) {
      setInd(prev => ({ ...prev, pip_associate_relationship: firstAccepted.suggested_relationship }));
    }
  }, [firstAccepted, ind.pip_associate_relationship]);
  const pipUndecided = pipCheck.undecided.length;
  useEffect(() => {
    if (pipUndecided === 0) setErrors(prev => { const { pip_check: _, ...rest } = prev; return rest; });
  }, [pipUndecided]);

  const updateRow = (setter, idx, field, value) => {
    setter(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };
  const addRow    = (setter, blank) => setter(prev => [...prev, blank]);
  const removeRow = (setter, idx) => setter(prev => prev.filter((_, i) => i !== idx));

  // Clear a field's error as soon as the user starts editing it again.
  const clearErr = (k) => {
    if (errors[k]) setErrors(prev => { const { [k]: _, ...rest } = prev; return rest; });
  };
  const setIndField = (k, v) => { setInd(prev => ({ ...prev, [k]: v })); clearErr(k); };
  const setBizField = (k, v) => { setBiz(prev => ({ ...prev, [k]: v })); clearErr(k); };

  // Picking a country suggests the nationality category; it can be changed.
  const setNationality = (country) => {
    setInd(prev => ({ ...prev, nationality: country, nationality_category: categoryFromCountry(country) || prev.nationality_category }));
    clearErr('nationality_category');
  };
  const setUboNationality = (idx, country) => {
    setUbos(prev => prev.map((row, i) => i === idx
      ? { ...row, nationality: country, nationality_category: categoryFromCountry(country) || row.nationality_category }
      : row));
  };

  // Validate required fields client-side; return an errors object (empty if OK).
  const validate = () => {
    const e = {};
    if (isInd) {
      if (!ind.first_name?.trim()) e.first_name = 'Full name is required';
      if (!ind.last_name?.trim())  e.last_name  = 'Surname is required';
      if (!ind.id_type)            e.id_type    = 'ID type is required';
      if (!ind.id_number?.trim())  e.id_number  = 'ID / Passport number is required';
      if (!ind.nationality_category)        e.nationality_category        = 'Nationality is required';
      if (pipCheck.loading)                 e.pip_check = 'The PIP check is still running — wait for it to finish';
      else if (pipCheck.error)              e.pip_check = 'The PIP check failed — try again';
      else if (!pipCheck.upToDate)          e.pip_check = 'The PIP check has not run yet';
      else if (pipCheck.undecided.length)   e.pip_check = 'Accept or reject every possible PIP record';
      if (pipCheck.isAssociate && !ind.pip_associate_relationship) {
        e.pip_associate_relationship = 'Choose how the client is associated with the PIP';
      }
      if (!ind.employment_status)           e.employment_status           = 'Occupation is required';
      if (!ind.source_of_funds)             e.source_of_funds             = 'Source of income is required';
      if (!ind.transaction_source_of_funds) e.transaction_source_of_funds = 'Source of funds for the transaction is required';
    } else {
      if (!biz.legal_name?.trim())  e.legal_name         = 'Legal name is required';
      if (!biz.business_type)       e.business_type      = 'Institution is required';
      if (!biz.nature_of_business)  e.nature_of_business = 'Nature of business is required';
      ubos.forEach((u, idx) => {
        if ((u.first_name || u.last_name) && !u.nationality_category) {
          e[`ubo_${idx}_nationality_category`] = 'BO nationality is required';
        }
      });
    }
    if (!productType)     e.product_type     = 'Product type is required';
    if (!deliveryChannel) e.delivery_channel = 'Delivery channel is required';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setError('Please fill in the highlighted required fields.');
      // Scroll the first errored field into view so the red highlight is visible.
      requestAnimationFrame(() => {
        const firstKey = Object.keys(fieldErrors)[0];
        const el = document.querySelector(`[data-field="${firstKey}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') el.focus();
        }
      });
      return;
    }
    setErrors({});

    const payload = {
      external_reference: externalRef || null,
      customer_type: customerType,
      kyc_tier: kycTier,
      product_type: productType,
      delivery_channel: deliveryChannel
    };
    // New customers omit blanks; edits send them as null so they are cleared.
    const fields = isEdit ? nullBlank : clean;
    if (isInd) {
      const { pip_associate_relationship, ...rest } = ind;
      payload.individual = {
        ...fields(rest),
        pip_declared: pipCheck.isPip,
        pip_associate_relationship: pipCheck.isAssociate ? pip_associate_relationship : 'no',
        pip_matches: pipCheck.payload()
      };
    } else {
      payload.business = {
        ...fields(biz),
        directors: directors.filter(d => d.first_name && d.last_name).map(clean),
        shareholders: shareholders.filter(s => s.full_name).map(clean),
        ubos: ubos.filter(u => u.first_name && u.last_name).map(clean)
      };
    }

    try {
      setSubmitting(true);
      if (isEdit) {
        const res = await axios.put(`/customers/${customer.id}`, payload);
        if (onUpdated) onUpdated(res.data);
      } else {
        const res = await axios.post('/customers', payload);
        onCreated(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Required dropdown of sheet options, with inline error.
  const riskSelect = ({ field, label, value, onChange, options, help }) => (
    <div className="form-group">
      <label className="required">{label}</label>
      <select
        data-field={field}
        value={value}
        style={errors[field] ? ERR_STYLE : undefined}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">Select…</option>
        {options.map(riskOption)}
      </select>
      {help && <span className="form-help">{help}</span>}
      {errors[field] && <span className="form-error">{errors[field]}</span>}
    </div>
  );

  const textInput = ({ field, label, value, onChange, required }) => (
    <div className="form-group">
      <label className={required ? 'required' : undefined}>{label}</label>
      <input
        data-field={field}
        value={value}
        style={errors[field] ? ERR_STYLE : undefined}
        onChange={e => onChange(e.target.value)}
      />
      {errors[field] && <span className="form-error">{errors[field]}</span>}
    </div>
  );

  return (
    <form onSubmit={submit}>
      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* ── Basic info ── */}
      <section className="form-section">
        <header className="form-section__head">
          <h4 className="form-section__title">Customer Basics</h4>
          <p className="form-section__subtitle">Type and how this customer will be referenced.</p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Customer Type</label>
            <select value={customerType} disabled={isEdit} onChange={e => { setCustomerType(e.target.value); setErrors({}); }}>
              {CUSTOMER_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <span className="form-help">
              {isEdit ? "The customer type can't be changed." : "Individual = Client; every other type = Legal Person."}
            </span>
          </div>
          <div className="form-group">
            <label>External Reference</label>
            <input type="text" value={externalRef} onChange={e => setExternalRef(e.target.value)} placeholder="Any reference you use for this customer (optional)" />
          </div>
          <div className="form-group">
            <label>KYC Tier</label>
            <select value={kycTier} onChange={e => setKycTier(e.target.value)}>
              <option value="lite">Lite</option>
              <option value="medium">Medium</option>
              <option value="full">Full</option>
            </select>
          </div>
        </div>
      </section>

      {isInd ? (
        <>
          {/* ── Client: identity ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Client</h4>
              <p className="form-section__subtitle">As shown on the client's identification document.</p>
            </header>
            <div className="form-grid-2">
              {textInput({ field: 'first_name', label: 'First name', value: ind.first_name, onChange: v => setIndField('first_name', v), required: true })}
              {textInput({ field: 'middle_name', label: 'Middle name(s)', value: ind.middle_name, onChange: v => setIndField('middle_name', v) })}
              {textInput({ field: 'last_name', label: 'Surname', value: ind.last_name, onChange: v => setIndField('last_name', v), required: true })}
              {textInput({ field: 'maiden_name', label: 'Maiden name', value: ind.maiden_name, onChange: v => setIndField('maiden_name', v) })}
              <div className="form-group">
                <label className="required">ID type</label>
                <select
                  data-field="id_type"
                  value={ind.id_type}
                  style={errors.id_type ? ERR_STYLE : undefined}
                  onChange={e => setIndField('id_type', e.target.value)}
                >
                  {ID_TYPES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                {errors.id_type && <span className="form-error">{errors.id_type}</span>}
              </div>
              {textInput({ field: 'id_number', label: 'ID / Passport number', value: ind.id_number, onChange: v => setIndField('id_number', v), required: true })}
              <div className="form-group">
                <label>Date of birth</label>
                <input type="date" value={ind.date_of_birth} onChange={e => setIndField('date_of_birth', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Country of residence</label>
                <CountryAutocomplete
                  value={ind.country_of_residence}
                  onChange={v => setIndField('country_of_residence', v)}
                  placeholder="Start typing a country…"
                />
              </div>
            </div>
          </section>

          {/* ── Client: risk inputs ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Client Risk Rating Inputs</h4>
              <p className="form-section__subtitle">
                The six dropdowns of the Client section. Each is rated as shown; together
                they give the Client risk rating. A PIP or PIP associate is always High.
              </p>
            </header>
            <div className="form-grid-3">
              <div className="form-group">
                <label>Nationality (country)</label>
                <CountryAutocomplete
                  value={ind.nationality}
                  onChange={setNationality}
                  placeholder="Start typing a country…"
                />
                <span className="form-help">Picking a country suggests the category.</span>
              </div>
              {riskSelect({
                field: 'nationality_category', label: 'Nationality', value: ind.nationality_category,
                onChange: v => setIndField('nationality_category', v), options: NATIONALITY_CATEGORIES
              })}
              {riskSelect({
                field: 'employment_status', label: 'Occupation', value: ind.employment_status,
                onChange: v => setIndField('employment_status', v), options: OCCUPATIONS
              })}
              {riskSelect({
                field: 'source_of_funds', label: 'Source of Income', value: ind.source_of_funds,
                onChange: v => setIndField('source_of_funds', v), options: SOURCES_OF_INCOME
              })}
              {riskSelect({
                field: 'transaction_source_of_funds', label: 'Source of Funds for the Transaction', value: ind.transaction_source_of_funds,
                onChange: v => setIndField('transaction_source_of_funds', v), options: TRANSACTION_SOURCES_OF_FUNDS
              })}
              <div className="form-group">
                <label>Monthly income band</label>
                <select value={ind.monthly_income_band} onChange={e => setIndField('monthly_income_band', e.target.value)}>
                  {isEdit && <option value="">Not captured</option>}
                  {INCOME.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                <span className="form-help">Not part of the rating; used by transaction monitoring.</span>
              </div>
              <PipCheckPanel
                check={pipCheck}
                relationship={ind.pip_associate_relationship}
                onRelationship={v => setIndField('pip_associate_relationship', v)}
                errors={errors}
                errStyle={ERR_STYLE}
              />
            </div>
          </section>
        </>
      ) : (
        <>
          {/* ── Legal person ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Legal Person</h4>
              <p className="form-section__subtitle">
                Institution and nature of business, with the beneficial owners below, give the
                Legal Person risk rating.
              </p>
            </header>
            <div className="form-grid-2">
              {textInput({ field: 'legal_name', label: 'Legal name', value: biz.legal_name, onChange: v => setBizField('legal_name', v), required: true })}
              {textInput({ field: 'trading_name', label: 'Trading name', value: biz.trading_name, onChange: v => setBizField('trading_name', v) })}
              {riskSelect({
                field: 'business_type', label: 'Institution', value: biz.business_type,
                onChange: v => setBizField('business_type', v), options: INSTITUTION_TYPES
              })}
              {textInput({ field: 'registration_number', label: 'Registration number', value: biz.registration_number, onChange: v => setBizField('registration_number', v) })}
              {riskSelect({
                field: 'nature_of_business', label: 'Nature of Business', value: biz.nature_of_business,
                onChange: v => setBizField('nature_of_business', v), options: NATURE_OF_BUSINESS
              })}
              {textInput({ field: 'tax_number', label: 'Tax number', value: biz.tax_number, onChange: v => setBizField('tax_number', v) })}
              <div className="form-group">
                <label>Country of incorporation</label>
                <CountryAutocomplete
                  value={biz.country_of_incorporation}
                  onChange={v => setBizField('country_of_incorporation', v)}
                  placeholder="Start typing a country…"
                />
              </div>
              {textInput({ field: 'source_of_funds', label: 'Source of funds', value: biz.source_of_funds, onChange: v => setBizField('source_of_funds', v) })}
              <div className="form-group">
                <label>Primary contact email</label>
                <input type="email" value={biz.primary_contact_email} onChange={e => setBizField('primary_contact_email', e.target.value)} />
              </div>
              {textInput({ field: 'primary_contact_phone', label: 'Primary contact phone', value: biz.primary_contact_phone, onChange: v => setBizField('primary_contact_phone', v) })}
            </div>
          </section>

          {/* ── Beneficial owners ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Beneficial Owners (BO)</h4>
              <p className="form-section__subtitle">
                Natural persons with ≥25% interest or effective control. The riskiest BO status
                and nationality are rated; no BO captured counts as High. Empty rows are ignored.
              </p>
            </header>
            {ubos.map((u, idx) => (
              <div key={idx} className="form-grid-3" style={{ alignItems: 'end', marginBottom: '0.75rem' }}>
                <div className="form-group"><label>First name</label>
                  <input value={u.first_name} onChange={e => updateRow(setUbos, idx, 'first_name', e.target.value)} /></div>
                <div className="form-group"><label>Last name</label>
                  <input value={u.last_name} onChange={e => updateRow(setUbos, idx, 'last_name', e.target.value)} /></div>
                <div className="form-group"><label>BO</label>
                  <select value={u.pip_status} onChange={e => updateRow(setUbos, idx, 'pip_status', e.target.value)}>
                    {BO_PIP_OPTIONS.map(riskOption)}
                  </select></div>
                <div className="form-group"><label>BO nationality (country)</label>
                  <CountryAutocomplete
                    value={u.nationality}
                    onChange={v => setUboNationality(idx, v)}
                    placeholder="Start typing a country…"
                  /></div>
                <div className="form-group"><label>BO Nationality</label>
                  <select
                    data-field={`ubo_${idx}_nationality_category`}
                    value={u.nationality_category}
                    style={errors[`ubo_${idx}_nationality_category`] ? ERR_STYLE : undefined}
                    onChange={e => { updateRow(setUbos, idx, 'nationality_category', e.target.value); clearErr(`ubo_${idx}_nationality_category`); }}
                  >
                    <option value="">Select…</option>
                    {NATIONALITY_CATEGORIES.map(riskOption)}
                  </select>
                  {errors[`ubo_${idx}_nationality_category`] && <span className="form-error">{errors[`ubo_${idx}_nationality_category`]}</span>}
                </div>
                <div className="form-group"><label>% interest</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input style={{ flex: 1 }} type="number" min="0" max="100" step="0.001" value={u.beneficial_interest_pct} onChange={e => updateRow(setUbos, idx, 'beneficial_interest_pct', e.target.value)} />
                    <button type="button" className="btn-cancel" onClick={() => removeRow(setUbos, idx)} disabled={ubos.length === 1} title="Remove">×</button>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="export-button" onClick={() => addRow(setUbos, BLANK_UBO)}>+ Add BO</button>
          </section>

          {/* ── Directors ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Directors</h4>
              <p className="form-section__subtitle">Empty rows are ignored on save.</p>
            </header>
            {directors.map((d, idx) => (
              <div key={idx} className="form-grid-3" style={{ alignItems: 'end', marginBottom: '0.5rem' }}>
                <div className="form-group"><label>First name</label>
                  <input value={d.first_name} onChange={e => updateRow(setDirectors, idx, 'first_name', e.target.value)} /></div>
                <div className="form-group"><label>Last name</label>
                  <input value={d.last_name} onChange={e => updateRow(setDirectors, idx, 'last_name', e.target.value)} /></div>
                <div className="form-group" style={{ position: 'relative' }}><label>Role</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input style={{ flex: 1 }} value={d.role_title} onChange={e => updateRow(setDirectors, idx, 'role_title', e.target.value)} />
                    <button type="button" className="btn-cancel" onClick={() => removeRow(setDirectors, idx)} disabled={directors.length === 1} title="Remove">×</button>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="export-button" onClick={() => addRow(setDirectors, { first_name: '', last_name: '', role_title: '' })}>+ Add director</button>
          </section>

          {/* ── Shareholders ── */}
          <section className="form-section">
            <header className="form-section__head">
              <h4 className="form-section__title">Shareholders</h4>
            </header>
            {shareholders.map((s, idx) => (
              <div key={idx} className="form-grid-2" style={{ alignItems: 'end', marginBottom: '0.5rem' }}>
                <div className="form-group"><label>Full name</label>
                  <input value={s.full_name} onChange={e => updateRow(setShareholders, idx, 'full_name', e.target.value)} /></div>
                <div className="form-group"><label>% holding</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input style={{ flex: 1 }} type="number" min="0" max="100" step="0.001" value={s.percentage} onChange={e => updateRow(setShareholders, idx, 'percentage', e.target.value)} />
                    <button type="button" className="btn-cancel" onClick={() => removeRow(setShareholders, idx)} disabled={shareholders.length === 1} title="Remove">×</button>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="export-button" onClick={() => addRow(setShareholders, { full_name: '', percentage: '' })}>+ Add shareholder</button>
          </section>
        </>
      )}

      {/* ── Product ── */}
      <section className="form-section">
        <header className="form-section__head">
          <h4 className="form-section__title">Product</h4>
          <p className="form-section__subtitle">Product type and delivery channel give the Product risk rating.</p>
        </header>
        <div className="form-grid-2">
          {riskSelect({
            field: 'product_type', label: 'Product type', value: productType,
            onChange: v => { setProductType(v); clearErr('product_type'); }, options: PRODUCT_TYPES
          })}
          {riskSelect({
            field: 'delivery_channel', label: 'Delivery channel', value: deliveryChannel,
            onChange: v => { setDeliveryChannel(v); clearErr('delivery_channel'); }, options: DELIVERY_CHANNELS
          })}
        </div>
      </section>

      <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button
          type="submit"
          className={`export-button${submitting ? ' is-loading' : ''}`}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : (isEdit ? 'Update Customer' : 'Create Customer')}
        </button>
      </div>
    </form>
  );
}

export default CustomerForm;
